/**
 * Redux slice for Assessment Timeline state.
 *
 * Manages:
 * - Timeline milestones for active sleep study orders
 * - Loading and error states for timeline data
 *
 * Pattern: Uses createAsyncThunk for data fetching.
 * Components dispatch thunks instead of using useEffect.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAssessmentTimeline } from '../../services/assessmentTimelineService';
const initialState = {
    milestones: [],
    loading: false,
    error: null,
    lastFetchedAt: null
};
/**
 * Async thunk to fetch timeline milestones
 */
export const fetchTimeline = createAsyncThunk('timeline/fetchTimeline', (patientId_1, _a) => __awaiter(void 0, [patientId_1, _a], void 0, function* (patientId, { rejectWithValue }) {
    try {
        const milestones = yield getAssessmentTimeline(patientId);
        return milestones;
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch timeline';
        return rejectWithValue(errorMessage);
    }
}));
const timelineSlice = createSlice({
    name: 'timeline',
    initialState,
    reducers: {
        /**
         * Set loading state
         */
        setLoading: (state, action) => {
            state.loading = action.payload;
            if (action.payload) {
                state.error = null;
            }
        },
        /**
         * Set timeline milestones after successful fetch
         */
        setMilestones: (state, action) => {
            state.milestones = action.payload;
            state.loading = false;
            state.error = null;
            state.lastFetchedAt = Date.now();
        },
        /**
         * Add a single milestone (for real-time updates)
         */
        addMilestone: (state, action) => {
            const exists = state.milestones.some((m) => m.id === action.payload.id);
            if (!exists) {
                state.milestones.push(action.payload);
                // Sort by displayOrder descending
                state.milestones.sort((a, b) => b.displayOrder - a.displayOrder);
            }
        },
        /**
         * Set error state
         */
        setError: (state, action) => {
            state.error = action.payload;
            state.loading = false;
        },
        /**
         * Clear error state
         */
        clearError: (state) => {
            state.error = null;
        },
        /**
         * Clear all timeline state
         */
        clearTimeline: () => {
            return Object.assign({}, initialState);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTimeline.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchTimeline.fulfilled, (state, action) => {
            state.milestones = action.payload;
            state.loading = false;
            state.error = null;
            state.lastFetchedAt = Date.now();
        })
            .addCase(fetchTimeline.rejected, (state, action) => {
            state.error = action.payload;
            state.loading = false;
        });
    }
});
export const { setLoading, setMilestones, addMilestone, setError, clearError, clearTimeline } = timelineSlice.actions;
export default timelineSlice.reducer;
